import { useState, useCallback, useRef, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext.jsx';
import { BASE } from '../api.js';
import { checkAndResize, isProbablyHeic, fileToJpegBlob } from '../utils/imageResize.js';

/* --- 环境判定：只有真 Capacitor 容器才走原生相机桥；其余环境（普通浏览器/iPad/微信 WebView）
   保留拍照按钮，但降级到 <input type file capture environment> 唤起系统相机，避免桥不存在时静默悬挂 --- */
function detectNativeEnv() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (ua.toLowerCase().includes('capacitor')) return true; // Capacitor 原生容器
  return false; // 普通浏览器 / iPad / 微信 WebView / Android 浏览器
}
const isNativeEnv = detectNativeEnv();
// 拍照走 Web 兜底：移动端 <input capture environment> 直接唤起系统相机
const webCaptureAttr = { capture: 'environment' };

/* --- 常量 --- */
const ANALYSIS_STEPS = [
  '分析脸型',
  '分析三庭五眼比例',
  '分析眉形',
  '分析眼型',
  '分析肤质',
  '分析五官对称度',
];
const STEP_DELAYS = [0, 650, 1500, 1900, 2700, 3200];
const API_TIMEOUT_MS = 120_000;

/* --- 辅助函数 --- */
function resizeDataUrl(dataUrl, maxSide = 1024) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const { width, height } = img;
      if (width >= 512 && height >= 512 && width <= maxSide && height <= maxSide) {
        resolve(dataUrl);
        return;
      }
      let newWidth, newHeight;
      if (width >= height) {
        newWidth = maxSide;
        newHeight = Math.round((height / width) * maxSide);
      } else {
        newHeight = maxSide;
        newWidth = Math.round((width / height) * maxSide);
      }
      if (newWidth < 512 || newHeight < 512) {
        const scale = 512 / Math.max(width, height);
        newWidth = Math.round(width * scale);
        newHeight = Math.round(height * scale);
        if (newWidth < 512 || newHeight < 512) { resolve(dataUrl); return; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = newWidth;
      canvas.height = newHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, newWidth, newHeight);
      resolve(canvas.toDataURL('image/jpeg', 0.9));
    };
    img.onerror = () => { resolve(dataUrl); };
    img.src = dataUrl;
  });
}

async function callAnalyze(base64, token, signal) {
  
  if (typeof base64 !== 'string' || !base64) {
    throw new Error('图片数据异常，请重新选择照片');
  }
  // 本地调试模式跳过 token 检查
  const useLocalDebug = new URL(window.location.href).searchParams.get('local') === '1';
  if (!useLocalDebug && !token) {
    throw new Error('请先登录');
  }
  const res = await fetch(BASE + '/tier1/analyze', { credentials: 'include',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ photo: base64 }),
    signal,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    if (res.status === 429 && err.error === 'daily_limit_exceeded') {
      throw new Error(err.message || '今日初识次数已用完，明天再来吧');
    }
    throw new Error(err.message || err.error || `请求失败: ${res.status}`);
  }
  return res.json();
}

/* --- 可复用的拍照/上传组件 --- */
export default function CapturePhotoUpload({ onComplete, onCancel, compact }) {
  const { token } = useContext(AuthContext);
  const [stage, setStage] = useState('select');
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [visibleCount, setVisibleCount] = useState(0);
  const [apiReady, setApiReady] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [reportId, setReportId] = useState(null);

  const controllerRef = useRef(null);
  const fileInputRef = useRef(null);
  const captureInputRef = useRef(null);
  const generationRef = useRef(0);
  const completionTimeoutRef = useRef(null);

  const startAnalysis = useCallback((base64) => {
    const gen = ++generationRef.current;
    setVisibleCount(0);
    setApiReady(false);
    setApiError(null);
    setReportData(null);
    setReportId(null);

    if (completionTimeoutRef.current !== null) {
      clearTimeout(completionTimeoutRef.current);
      completionTimeoutRef.current = null;
    }

    STEP_DELAYS.forEach((delay, i) => {
      setTimeout(() => setVisibleCount(i + 1), delay);
    });

    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    (async () => {
      try {
        const data = await callAnalyze(base64, token, controller.signal);

        if (generationRef.current !== gen) return;

        setReportData(data.report);
        setReportId(data.reportId ?? null);
        if (data.reportId && data.report) {
          sessionStorage.setItem('capture_report_' + data.reportId, JSON.stringify(data.report));
        }
        setApiReady(true);

        if (data.reportId) {
          sessionStorage.setItem('capture_report_id', data.reportId);
          sessionStorage.setItem('capture_preview', preview || '');
          // 同时存到 localStorage，确保跨标签页可用
          localStorage.setItem('capture_report_id', data.reportId);
          localStorage.setItem('capture_preview', preview || '');
          // 更新 URL 参数，方便分享和恢复
          if (typeof window !== 'undefined') {
            const url = new URL(window.location.href);
            url.searchParams.set('id', data.reportId);
            window.history.replaceState({}, '', url.toString());
          }
        }

        setStage('done');
      } catch (e) {
        if (generationRef.current !== gen) return;
        if (e.name === 'AbortError') return;
        setError(e.message);
        setApiError(e.message);
        setApiReady(false);
        setStage('error');
      }
    })();

    completionTimeoutRef.current = setTimeout(() => {
      completionTimeoutRef.current = null;
      if (generationRef.current === gen && !apiReady && !apiError && stage !== 'done' && stage !== 'error') {
        // 让本次 gen 作废：IIFE 成功路径里的 `if (generationRef.current !== gen) return;` 会丢弃迟到的结果，
        // 避免 120s 后 fetch 才返回成功时把界面从 error 翻回"分析完成"。
        generationRef.current = gen + 1;
        // 中止仍在途的请求（与 startAnalysis / handleRetry / handleCancel 共用同一个 controllerRef）
        controllerRef.current?.abort();
        // error 是模板 `error || '分析失败，请稍后重试'` 实际读取的状态，必须同时 set，
        // 否则只 setApiError 会回落默认文案、看不到"分析耗时较长"。
        setError('分析耗时较长，请稍后重试');
        setApiError('分析耗时较长，请稍后重试');
        setStage('error');
      }
    }, API_TIMEOUT_MS);
  }, [token, stage, apiReady, apiError, preview, onComplete]);


  // 拍照：原生走 Capacitor 桥，非原生降级到 <input capture> 唤起系统相机
  const handleCapturePhoto = useCallback(() => {
    if (isNativeEnv) {
      import('@capacitor/camera').then(({ Camera, CameraSource }) =>
        Camera.getPhoto({ quality: 85, allowEditing: false, resultType: 1, source: CameraSource.Camera })
          .then(async (r) => {
            const resized = await resizeDataUrl(r.dataUrl);
            setPreview(resized);
            setStage('analyzing');
            startAnalysis(resized);
          })
          .catch((err) => { setError(err.message); setStage('error'); })
      ).catch(() => captureInputRef.current?.click());
      return;
    }
    captureInputRef.current?.click();
  }, [startAnalysis]);

  // 从相册：原生走 Capacitor 桥，非原生降级到普通文件框
  const handleGalleryPhoto = useCallback(() => {
    if (isNativeEnv) {
      import('@capacitor/camera').then(({ Camera, CameraSource }) =>
        Camera.getPhoto({ quality: 85, allowEditing: false, resultType: 1, source: CameraSource.Photos })
          .then(async (r) => {
            const resized = await resizeDataUrl(r.dataUrl);
            setPreview(resized);
            setStage('analyzing');
            startAnalysis(resized);
          })
          .catch((err) => { setError(err.message); setStage('error'); })
      ).catch(() => fileInputRef.current?.click());
      return;
    }
    fileInputRef.current?.click();
  }, [startAnalysis]);

  const handleReupload = useCallback(() => {
    setStage('select');
    setError(null);
    setApiError(null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setTimeout(() => fileInputRef.current?.click(), 0);
  }, []);

  const handleFileChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // 先切到 analyzing 态，让"选照片 → 本地处理"这段时间有明确反馈，避免界面像卡死
    setStage('analyzing');
    setError(null);
    try {
      let dataUrl;
      if (await isProbablyHeic(file)) {
        const jpegBlob = await fileToJpegBlob(file);
        dataUrl = await checkAndResize(jpegBlob);
      } else {
        dataUrl = await checkAndResize(file);
      }
      if (typeof dataUrl !== 'string') throw new Error('图片处理失败，请重试');
      setPreview(dataUrl);
      setStage('analyzing');
      startAnalysis(dataUrl);
    } catch (err) {
      setError(err && err.message ? '照片处理失败：' + err.message : '照片处理失败，请换一张 JPG/PNG 试试');
      setStage('error');
    }
    e.target.value = '';
  }, [startAnalysis]); // setStage/setError 为稳定引用，无需加入依赖

  const handleRetry = useCallback(async () => {
    setError(null);
    setApiError(null);
    if (!preview) return;
    controllerRef.current?.abort();
    if (completionTimeoutRef.current !== null) {
      clearTimeout(completionTimeoutRef.current);
      completionTimeoutRef.current = null;
    }
    setStage('analyzing');
    try {
      const byteStr = atob(preview.split(',')[1]);
      const arr = new Uint8Array(byteStr.length);
      for (let i = 0; i < byteStr.length; i++) arr[i] = byteStr.charCodeAt(i);
      const blob = new Blob([arr], { type: 'image/jpeg' });
      const resizedDataUrl = await checkAndResize(blob);
      startAnalysis(resizedDataUrl);
    } catch (e) {
      setError(e.message);
      setStage('error');
    }
  }, [preview, startAnalysis]);

  const handleCancel = useCallback(() => {
    controllerRef.current?.abort();
    window.history.pushState({}, '', '/report');
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);

  if (stage === 'select') {
    return (
      <div className={compact ? 'capture-compact-area' : 'capture-select-area'}>
        <p className="capture-hint">请上传一张正面清晰自拍，方便我们为你生成美妆建议</p>
        <div className="capture-actions">
          <button
            className="capture-action-btn capture-camera-btn"
            onClick={handleCapturePhoto}
          >
            <span className="capture-icon">📷</span> 拍照
          </button>
          <button
            className="capture-action-btn capture-gallery-btn"
            onClick={handleGalleryPhoto}
          >
            <span className="capture-icon">🖼</span> 从相册选择
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>
          <input
            ref={captureInputRef}
            type="file"
            accept="image/*"
            {...webCaptureAttr}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
      </div>
    );
  }

  return (
    <div className="capture-inline-area">
      {preview && (
        <div className="capture-preview-wrap">
          <img className="capture-preview-img" src={preview} alt="预览" />
        </div>
      )}
      {stage === 'error' && (
        <div className="capture-error">
          <div className="capture-error-icon">⚠️</div>
          <p className="capture-error-text">{error || '分析失败，请稍后重试'}</p>
          <button className="capture-retry-btn" onClick={handleRetry}>重试</button>
          <button className="capture-retry-btn capture-reupload-btn" onClick={handleReupload}>重新选择</button>
          {!compact && <button className="capture-cancel-btn" onClick={handleCancel}>取消</button>}
        </div>
      )}
      {stage === 'analyzing' && !preview && (
        <div className="capture-analyzing capture-processing-hint">
          <p className="capture-pending-text">照片处理中…</p>
        </div>
      )}
      {stage === 'analyzing' && (
        <div className="capture-analyzing">
          <ul className="capture-steps">
            {ANALYSIS_STEPS.map((label, i) => (
              <li key={i} className={`capture-step${i < visibleCount ? ' capture-step--visible' : ''}`}>
                <span className="capture-step-check">{i < visibleCount ? '✓' : '·'}</span>
                <span className="capture-step-label">{label}</span>
              </li>
            ))}
          </ul>
          {visibleCount >= ANALYSIS_STEPS.length && !apiReady && !apiError && (
            <p className="capture-pending-text">正在整理结果中...</p>
          )}
        </div>
      )}
      {stage === 'done' && (
        <div className="capture-done">
          <div className="capture-checkmark">✓</div>
          <p className="capture-done-title">分析完成</p>
          <p className="capture-done-sub">已为你生成专属美妆建议</p>
          <button className="capture-view-report-btn" onClick={() => onComplete?.(reportId, reportData, preview)}>查看详情</button>
        </div>
      )}
    </div>
  );
}
